import React, { useMemo, useState } from 'react';
import { Home, Calculator as CalcIcon, Info } from 'lucide-react';
import { Container, Btn } from './ui';

const fmtUSD = (n) => `$${Math.round(n).toLocaleString('en-US')}`;
const fmtNum = (n) => Math.round(n).toLocaleString('en-US');
const DAYS_PER_MONTH = 30.42;

function Field({ id, label, hint, value, onChange, min, max, step = 1, slider = false, prefix }) {
  return (
    <div>
      <label htmlFor={id} className="text-sm font-semibold text-white">{label}</label>
      {hint && <p className="mt-0.5 text-xs text-slate-400">{hint}</p>}
      {slider && (
        <input
          type="range" min={min} max={max} step={step} value={Math.min(Number(value) || 0, max)}
          onChange={(e) => onChange(Number(e.target.value))}
          className="mt-3 h-2 w-full cursor-pointer appearance-none rounded-full bg-white/15 accent-brand"
          data-testid={`${id}-slider`}
        />
      )}
      <div className="mt-2 flex items-center rounded-xl border border-white/15 bg-white/5 px-3 focus-within:border-brand">
        {prefix && <span className="text-sm text-slate-400">{prefix}</span>}
        <input
          id={id} type="number" min={min} value={value}
          onChange={(e) => onChange(e.target.value)}
          className="h-11 w-full bg-transparent px-1 text-sm text-white outline-none"
          data-testid={`${id}-input`}
        />
      </div>
    </div>
  );
}

export default function VcrCalculator() {
  const [units, setUnits] = useState(800);
  const [vacantDays, setVacantDays] = useState(21);
  const [monthlyCost, setMonthlyCost] = useState(90);

  const est = useMemo(() => {
    const u = Math.max(0, Number(units) || 0);
    const d = Math.max(0, Number(vacantDays) || 0);
    const m = Math.max(0, Number(monthlyCost) || 0);
    const dailyCost = m / DAYS_PER_MONTH;
    const unitDays = u * d;
    const annual = unitDays * dailyCost;
    return { unitDays, dailyCost, annual, monthly: annual / 12 };
  }, [units, vacantDays, monthlyCost]);

  return (
    <section className="px-5 py-16 sm:px-8 sm:py-24">
      <Container className="px-0">
        <div className="relative overflow-hidden rounded-3xl border border-white/10 bg-brand-ink px-6 py-12 sm:px-10 sm:py-14">
          <div className="bp-grid-bg pointer-events-none absolute inset-0 opacity-30" />
          <div className="pointer-events-none absolute -right-24 -top-24 h-72 w-72 rounded-full bg-brand/25 blur-3xl" />
          <div className="relative">
            <div className="max-w-2xl">
              <span className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.18em] text-brand">
                <Home className="h-4 w-4" /> Vacant Cost Recovery
              </span>
              <h2 className="mt-4 font-display text-3xl font-bold leading-tight text-white sm:text-4xl">
                How much vacant utility spend could you recover?
              </h2>
              <p className="mt-3 text-base leading-relaxed text-slate-300">
                When a unit sits empty, its utility charges fall back on you. Estimate the spend you could recover across your portfolio with occupancy-aware, validated bill data.
              </p>
            </div>

            <div className="mt-10 grid grid-cols-1 gap-6 lg:grid-cols-5">
              {/* Inputs */}
              <div className="lg:col-span-3 grid grid-cols-1 gap-6 sm:grid-cols-2">
                <div className="sm:col-span-2">
                  <Field id="vcr-calc-units" label="Units in portfolio" slider min={0} max={5000} step={10}
                    value={units} onChange={setUnits} />
                </div>
                <Field id="vcr-calc-days" label="Avg vacant days / unit / year" hint="Industry turn + downtime, e.g. 14–30"
                  slider min={0} max={60} value={vacantDays} onChange={setVacantDays} />
                <Field id="vcr-calc-cost" label="Utility cost / vacant unit / month" hint="Electric, water, gas while empty"
                  prefix="$" min={0} value={monthlyCost} onChange={setMonthlyCost} />
              </div>

              {/* Result */}
              <div className="lg:col-span-2 flex flex-col rounded-2xl border border-brand/30 bg-white/[0.04] p-6 sm:p-7">
                <span className="text-xs font-semibold uppercase tracking-[0.18em] text-brand">
                  <CalcIcon className="mr-1.5 inline h-3.5 w-3.5 align-[-1px]" /> Est. recoverable / year
                </span>
                <div className="mt-2 font-display text-4xl font-bold text-white" data-testid="vcr-calc-result-annual">
                  {fmtUSD(est.annual)}
                </div>
                <div className="mt-3 space-y-2 text-sm text-slate-300">
                  <div className="flex items-center justify-between">
                    <span>≈ per month</span>
                    <span className="font-semibold text-white">{fmtUSD(est.monthly)}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span>Vacant unit-days / yr</span>
                    <span className="font-semibold text-white">{fmtNum(est.unitDays)}</span>
                  </div>
                  <div className="flex items-center justify-between border-t border-white/10 pt-2">
                    <span>Utility cost / vacant day</span>
                    <span className="font-semibold text-white">{fmtUSD(est.dailyCost)}</span>
                  </div>
                </div>
                <Btn to="/demo" variant="white" full arrow className="mt-6">Recover it on your portfolio</Btn>
                <p className="mt-4 text-[11px] leading-relaxed text-slate-400">
                  <Info className="mr-1 inline h-3 w-3 align-[-1px]" />
                  Illustrative estimate for planning only — not a guarantee of recovery. Actual results depend on occupancy data, tariffs and recoverability rules.
                </p>
              </div>
            </div>
          </div>
        </div>
      </Container>
    </section>
  );
}
